/* =========================================================================
   Ribbon scroll affordance controller.
   Wires prev/next arrows + edge-fade state onto any element marked
   [data-ribbon-wrapper] that contains a [data-ribbon-scroll] scroller and
   (optionally) [data-ribbon-prev] / [data-ribbon-next] buttons.
   ========================================================================= */
(function () {
  // Included by several sections; only wire up global listeners once. The flag
  // lives on `window` rather than in this closure because the guard has to hold
  // across separate evaluations of this file, not just within one.
  /** @type {Window & { __ribbonScrollLoaded?: boolean }} */
  var w = window;
  if (w.__ribbonScrollLoaded) return;
  w.__ribbonScrollLoaded = true;

  /** A ribbon wrapper, tagged once wired so a re-scan skips it. */
  /** @typedef {Element & { __ribbonInit?: boolean }} RibbonWrapper */

  var EPS = 2; // px slack so sub-pixel rounding doesn't leave an arrow stuck on

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /** @param {RibbonWrapper} wrapper */
  function init(wrapper) {
    if (wrapper.__ribbonInit) return;
    // The guard on the next line is what actually makes this non-null; the
    // cast just carries that into update()/page(), which read `scroller` from
    // the enclosing scope and so are checked without the narrowing.
    var scroller = /** @type {Element} */ (wrapper.querySelector('[data-ribbon-scroll]'));
    if (!scroller) return;
    wrapper.__ribbonInit = true;

    var prev = /** @type {HTMLButtonElement | null} */ (
      wrapper.querySelector('[data-ribbon-prev]')
    );
    var next = /** @type {HTMLButtonElement | null} */ (
      wrapper.querySelector('[data-ribbon-next]')
    );

    function update() {
      var max = scroller.scrollWidth - scroller.clientWidth;
      var x = scroller.scrollLeft;
      var scrollable = max > EPS + 2;

      wrapper.classList.toggle('ribbon--scrollable', scrollable);
      wrapper.classList.toggle('ribbon--at-start', x <= EPS);
      wrapper.classList.toggle('ribbon--at-end', x >= max - EPS);

      if (prev) prev.disabled = !scrollable || x <= EPS;
      if (next) next.disabled = !scrollable || x >= max - EPS;
    }

    /** @param {number} direction -1 to page left, 1 to page right. */
    function page(direction) {
      var amount = scroller.clientWidth * 0.8;
      scroller.scrollBy({
        left: direction * amount,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    if (prev) {
      prev.addEventListener('click', function () {
        page(-1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        page(1);
      });
    }

    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    window.addEventListener('load', update);

    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(scroller);
    }

    // Items can come and go after load — menu-visibility.js drops category
    // tabs whose collections have nothing to show, and collection-nav.js
    // re-renders the ribbon on an in-place category switch. Either changes
    // scrollWidth without resizing the scroller's own box, so the
    // ResizeObserver above never fires and the arrows keep a stale state.
    if ('MutationObserver' in window) {
      new MutationObserver(update).observe(scroller, { childList: true });
    }

    // Recompute once late-loading images change the scroll width.
    Array.prototype.forEach.call(scroller.querySelectorAll('img'), function (img) {
      if (!img.complete) {
        img.addEventListener('load', update, { once: true });
      }
    });

    update();
  }

  /**
   * @param {EventTarget | null} [root] Subtree to scan — the re-rendered
   *   section from `shopify:section:load`. Defaults to the whole document.
   */
  function initAll(root) {
    var scope = root instanceof Element ? root : document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-ribbon-wrapper]'),
      init
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: re-init when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
